import cn from 'clsx';
import { faArrowUpLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC } from 'react';
import { Item } from '@v2/shared/lib/useAutoComplete';

type Props = {
  open: boolean;
  listboxId: string;
  itemsContainerProps: {};
  items: Item[];
  onClick: (item?: Item) => void;
  isSelected: (index: number) => boolean;
  isSpacer: (index: number) => boolean;
};

const SearchSuggestionsPanel: FC<Props> = ({
  open,
  listboxId,
  itemsContainerProps,
  items,
  onClick,
  isSelected,
  isSpacer,
}) => {
  if (!open || items.length === 0) {
    return null;
  }

  return (
    <ul
      id={listboxId}
      role="listbox"
      aria-label="Search suggestions"
      className={cn(
        'absolute top-12 inset-x-0 w-full rounded-md z-[280] overflow-hidden shadow-xl',
        'bg-white p-1',
        'border-slate-900 border-[1.5px]'
      )}
      {...itemsContainerProps}
    >
      {items.map((item, key) => {
        const selected = isSelected(key);

        if (isSpacer(key)) {
          return <li key={key} role="separator" className="border-b border-slate-200 my-1"></li>;
        }

        return (
          <li
            key={key}
            id={`${listboxId.replace('-listbox', '')}-option-${key}`}
            role="option"
            aria-selected={selected}
            className={cn(
              'group',
              'flex justify-between items-start',
              'text-sm font-medium px-3 py-2 cursor-pointer',
              'rounded-md',
              selected
                ? 'bg-red-100 text-red-900'
                : 'bg-white hover:bg-slate-100 active:bg-red-100 text-slate-800 hover:text-slate-900 active:text-red-900'
            )}
            onClick={() => onClick(item)}
          >
            {/*
              A suggestion is a title, and optionally a cover and a line of detail beside
              it — see `Item`. The site search passes neither and renders exactly the row
              it always did; the sale's scoped search passes both, because it is choosing
              between products rather than completing a phrase.
            */}
            {item.image && (
              <img
                src={item.image}
                alt=""
                aria-hidden="true"
                width={36}
                height={46}
                className="w-9 h-[46px] object-contain shrink-0 mr-3"
                loading="lazy"
              />
            )}
            {item.detail ? (
              <span className="min-w-0 flex-grow">
                <span className="block">{item.title}</span>
                <span className="block font-normal mt-0.5">{item.detail}</span>
              </span>
            ) : (
              <span>{item.title}</span>
            )}
            <FontAwesomeIcon
              icon={faArrowUpLeft}
              className={cn(
                'text-xl shrink-0',
                selected ? 'text-red-900' : 'text-slate-500 group-hover:text-slate-600 group-active:text-red-900'
              )}
            />
          </li>
        );
      })}
    </ul>
  );
};

export default SearchSuggestionsPanel;
